'use client';

import { twMerge } from '@shared/themes';
import type { Currency } from '@shared/utils';
import { useTranslations } from 'next-intl';
import type { ComponentProps } from 'react';
import type { LotteryName } from '../../../lottery-purchases/types';
import type { AdditionalDrawingInfo, ExtraGame, OddsEntry, ResultsConfig } from '../../types';
import { PrizeAnnotations } from './components/PrizeAnnotations/PrizeAnnotations';
import { PrizeTablesProvider } from './context';
import { resolvePrizeTableStrategy } from './utils';

type PrizeTablesProps = ComponentProps<'div'> & {
  data: OddsEntry[];
  drawingDate: string;
  lotteryName: LotteryName;
  currency: Currency;
  extraGames?: ExtraGame[];
  config?: ResultsConfig;
  additionalDrawingInfo?: AdditionalDrawingInfo;
};

/**
 * PrizeTables component renders the prize tables for a lottery.
 * This component makes use of PrizeTableStrategy set-up, where proper UI component is rendered
 * and data is mapped to appropriate format based on the lottery.
 * @param data - The data array to be rendered in the prize tables.
 * @param drawingDate - The date of the drawing.
 * @param lotteryName - The name of the lottery.
 * @param currency - The currency used for the lottery.
 * @param extraGames - (Optional) The extra games played in the lottery (if any).
 * @param config - (Optional) The configuration for the give lottery results page.
 * @param additionalDrawingInfo - (Optional) Additional information about the drawing (from BE).
 * @returns The HTMLDivElement element as container with prize tables inside
 */
function PrizeTables({ className, data, drawingDate, extraGames = [], lotteryName, currency, config, additionalDrawingInfo, ...props }: PrizeTablesProps) {
  const namespace = `lotteries.${lotteryName}`;
  const t = useTranslations(namespace);
  const { dataMapper, Component, multipliersConfig, tooltips } = resolvePrizeTableStrategy({ lotteryName, additionalDrawingInfo });
  const drawingTimes = additionalDrawingInfo?.originalDrawingTimes ?? [];

  const providerValue = {
    lotteryName,
    drawingDate,
    extraGames,
    currency,
    data,
    config,
    dataMapper,
    namespace,
    multipliers: multipliersConfig?.[0] ?? [],
    multiplier: multipliersConfig?.[1] ?? 1,
    additionalDrawingInfo,
    className: 'flex flex-col gap-y-5',
    ...(tooltips ? { tooltips: tooltips(t) } : {}),
  };

  return (
    <div className={twMerge('flex flex-col gap-8', className)} {...props}>
      <PrizeTablesProvider value={providerValue}>
        <Component />
      </PrizeTablesProvider>
      <PrizeAnnotations lotteryName={lotteryName} drawingDate={drawingDate} drawingTimes={drawingTimes} />
    </div>
  );
}

export { PrizeTables };
